import Box from "@mui/material/Box";
import useWorkoutPages from "./useWorkoutPages";
import {
  MenuList,
  WfIconContentInfoRegular,
  WfIconNavigationCloseRegular,
} from "@WahooFitness/redesignr";
import {
  useHeaderContext,
  GlobalHeader,
  ErrorBoundary,
  useSnackbarContext,
  postClose,
} from "@WahooFitness/wahoo-offline-mfe";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableItem from "../../SortableItem";
import { useCallback } from "react";
import { t, Trans } from "@lingui/macro";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import DeleteCustomPageDrawer from "./Drawers/DeleteCustomPageDrawer";
import WorkoutPagesActionMenu from "./WorkoutPagesActionMenu";
import { motion } from "motion/react";
import { Alert, Container, Stack } from "@mui/material";
const WorkoutPages = ({
  elevation,
  openAsDrawer,
}: {
  elevation?: boolean;
  openAsDrawer?: boolean;
}) => {
  const { navigateBack } = useHeaderContext();
  const {
    pageMenuLists,
    flushPendingSave,
    addCustomPage,
    resetPagesToDefault,
    showDeleteDialog,
    onCloseDeleteDialog,
    deletePage,
    pagesAreLoading,
    pagesError,
    maxPageNameLength,
    pageToDelete,
    setPageToDelete,
    sensors,
    handleDragStart,
    handleDragEnd,
    activeId,
  } = useWorkoutPages(elevation);

  const { enqueueSnackbar } = useSnackbarContext();

  const handleBackAction = useCallback(async () => {
    await flushPendingSave();
    navigateBack();
  }, [flushPendingSave, navigateBack]);

  const closeDrawer = useCallback(() => postClose("workoutProfiles"), []);

  return (
    <>
      <GlobalHeader
        title={t`Workout pages`}
        primaryRightActionItem={
          elevation
            ? undefined
            : {
                componentOverride: (
                  <WorkoutPagesActionMenu
                    resetPagesToDefault={resetPagesToDefault}
                    maxPageNameLength={maxPageNameLength}
                    addCustomPage={addCustomPage}
                  />
                ),
              }
        }
        primaryLeftActionItem={
          openAsDrawer
            ? {
                icon: <WfIconNavigationCloseRegular />,
                action: closeDrawer,
              }
            : undefined
        }
        backAction={handleBackAction}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box height="100%" width="100%">
          <ErrorBoundary error={pagesError} isLoading={pagesAreLoading}>
            <Box minHeight="100%" maxWidth="sm" display="flex" flexDirection="column" p={2}>
              <Box pb={3}>
                <Alert severity="info" icon={<WfIconContentInfoRegular />} variant="filled">
                  <Trans>
                    Some pages and data fields are available only when recording with an ELEMNT bike
                    computer.
                  </Trans>
                </Alert>
              </Box>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext
                  items={pageMenuLists.map((item) => item.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack gap={"1px"}>
                    {pageMenuLists.map((menuList, index) => (
                      <SortableItem id={menuList.key} key={menuList.key}>
                        <MenuList
                          sx={{
                            p: 0,
                            "& .MuiPaper-root.MuiPaper-rounded": {
                              borderRadius:
                                index === 0
                                  ? "8px 8px 0 0"
                                  : index === pageMenuLists.length - 1
                                    ? "0 0 8px 8px"
                                    : 0,
                            },
                          }}
                          listItems={menuList.pageListItems}
                        />
                      </SortableItem>
                    ))}
                  </Stack>
                </SortableContext>
                <DragOverlay dropAnimation={null}>
                  {activeId ? (
                    <motion.div
                      style={{ borderRadius: 12 }}
                      initial={{ scale: 1 }}
                      animate={{
                        scale: 1.05,
                        boxShadow:
                          "0 3px 14px 2px rgba(0, 0, 0, 0.08), 0 8px 10px 1px rgba(0, 0, 0, 0.08), 0 5px 5px -3px rgba(0, 0, 0, 0.12)",
                      }}
                      transition={{ type: "spring", duration: 0.2 }}
                    >
                      <MenuList
                        sx={{ p: 0, ".MuiPaper-root": { borderRadius: 2 } }}
                        listItems={
                          pageMenuLists.find((menuList) => menuList.key === activeId)
                            ?.pageListItems || []
                        }
                      />
                    </motion.div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </ErrorBoundary>
        </Box>
        <DeleteCustomPageDrawer
          pageName={pageToDelete?.name}
          handleCloseDrawer={onCloseDeleteDialog}
          handleDelete={async () => {
            await deletePage();
            onCloseDeleteDialog();
            setPageToDelete(undefined);
            enqueueSnackbar({
              message: t`${pageToDelete?.name} page deleted`,
              enableClickAway: true,
            });
          }}
          open={showDeleteDialog}
        />
      </Container>
    </>
  );
};

export default WorkoutPages;
